import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query-v4';
import apiFetch from '@scale/frontend-lib/api-fetch';
import INotificationSend, {
  NotificationApps,
  DEFAULT_NOTIFICATION_LIMIT,
  DEFAULT_NOTIFICATION_SKIP,
} from '@scale/shared/types/notifications/notificationSend';

interface UseOpsHubNotificationsParams {
  skip?: number;
  limit?: number;
  latestDate?: Date;
}

const POLLING_INTERVAL = 60000; // 1 minute polling

interface NotificationsResponse {
  notifications: INotificationSend[];
  total: number;
  unread: number;
}

export const useGetOpsHubNotifications = ({
  skip = DEFAULT_NOTIFICATION_SKIP,
  limit = DEFAULT_NOTIFICATION_LIMIT,
  latestDate,
}: UseOpsHubNotificationsParams = {}) => {
  return useQuery<NotificationsResponse>({
    queryKey: ['notifications', 'opshub', { skip, limit, latestDate }],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
        app: NotificationApps.OPS_HUB,
      });

      if (latestDate) {
        queryParams.append('latestDate', latestDate.toISOString());
      }

      const response = await apiFetch(null, `corp-api/notifications?${queryParams.toString()}`);
      return response;
    },
    refetchInterval: POLLING_INTERVAL,
  });
};

export const useDismissNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await apiFetch(null, `corp-api/notifications/dismiss/${notificationId}`, {
        method: 'PUT',
      });
    },
    onSuccess: () => {
      // Invalidate the notifications query to refetch
      queryClient.invalidateQueries({ queryKey: ['notifications', 'opshub'] });
    },
  });
};

export const useDismissAllNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiFetch(null, 'corp-api/notifications/dismiss', {
        method: 'PUT',
        body: { filters: { app: NotificationApps.OPS_HUB } },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'opshub'] });
    },
  });
};
